import React, { useMemo } from 'react'
import styles from './MicrositeCodeEditor.module.css'
import { Code2, AlertCircle } from 'lucide-react'

/**
 * MicrositeCodeEditor
 * Textarea styled as a dark code surface — JetBrains Mono, line
 * count, optional mode badge. Used for custom CSS, Schema.org JSON,
 * tracking script code, and HTML email bodies. Not a full editor
 * (no real syntax highlighting) — keeps things lightweight and
 * dependency-free while still feeling purpose-built for code.
 *
 * Usage:
 *   <MicrositeCodeEditor
 *     label="Custom CSS"
 *     mode="css"
 *     value={form.customCss}
 *     onChange={(v) => updateField('customCss', v)}
 *     rows={8}
 *   />
 */
const MicrositeCodeEditor = ({
    label,
    mode = 'text', // css | json | html | text
    value,
    onChange,
    rows = 8,
    placeholder = '',
    error,
    disabled = false
}) => {
    const lineCount = useMemo(() => (value ? value.split('\n').length : 1), [value])

    return (
        <div className={styles.field}>
            {label && (
                <div className={styles.labelRow}>
                    <label className={styles.label}>
                        <Code2 size={12} />
                        {label}
                    </label>
                    <span className={styles.modeBadge}>{mode}</span>
                </div>
            )}

            <div className={`${styles.editorWrap} ${error ? styles.editorError : ''}`}>
                <textarea
                    className={styles.textarea}
                    rows={rows}
                    value={value ?? ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    spellCheck={false}
                />
                <div className={styles.footer}>
                    <span>{lineCount} line{lineCount === 1 ? '' : 's'}</span>
                </div>
            </div>

            {error && (
                <span className={styles.errorText}>
                    <AlertCircle size={12} />
                    {error}
                </span>
            )}
        </div>
    )
}

export default MicrositeCodeEditor